import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useMemo,
    isValidElement,
} from "react";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import { NotificationCommand } from "../interfaces/NotificationBridgeInterface";
import { normalizeSettings } from "../helper/normalizeSettings";

/**
 * Context shape for global toast handling.
 */
export interface ToastContextState {
    /** Enqueue a new toast (uses current time as timestamp). */
    showToast: (cmd: NotificationCommand) => void;
    /** Clear all active toasts. */
    clearToasts: () => void;
}

export const ToastContext = createContext<ToastContextState | null>(null);

/**
 * Hook to access the toast API.
 * @throws Error if used outside of {@link ToastProvider}.
 */
export const useToast = (): ToastContextState => {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error("useToast must be used within ToastProvider");
    return ctx;
};

interface ToastProviderProps {
    children: ReactNode;
}

/** Internal item with runtime timestamp (no API fields invented). */
type ToastItem = {
    cmd: NotificationCommand;
    createdAt: number; // ms since epoch, set locally on enqueue
};

/**
 * Provider that renders and manages Bootstrap toasts based on NotificationCommands.
 * Uses only fields you provide (`settings`, `content`) and adds a local timestamp.
 */
export const ToastProvider = ({ children }: ToastProviderProps) => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    const showToast = (cmd: NotificationCommand) => {
        setToasts((prev) => [...prev, { cmd, createdAt: Date.now() }]);
    };

    const clearToasts = () => setToasts([]);

    const value = useMemo<ToastContextState>(() => ({ showToast, clearToasts }), []);

    return (
        <ToastContext.Provider value={value}>
            {children}

            <ToastContainer position="top-end" className="p-3">
                {toasts.map(({ cmd, createdAt }, idx) => {
                    const s = normalizeSettings(cmd.settings);
                    const delayMs = s.delay ? Number(s.delay) : 5000;
                    const variant = s.variant ?? "light"; // Bootstrap bg variants; falls nicht gesetzt → light
                    const cls = s.class ?? "";
                    const timeLabel = new Date(createdAt).toLocaleTimeString();

                    return (
                        <Toast
                            key={idx}
                            onClose={() => setToasts((prev) => prev.filter((_, i) => i !== idx))}
                            delay={delayMs}
                            autohide
                            bg={variant}
                            className={cls}
                        >
                            <Toast.Header closeButton>
                                <small className="text-muted ms-auto">{timeLabel}</small>
                            </Toast.Header>

                            <Toast.Body>
                                {typeof cmd.content === "string" ? (
                                    <div dangerouslySetInnerHTML={{ __html: cmd.content }} />
                                ) : isValidElement(cmd.content) ? (
                                    cmd.content
                                ) : null}
                            </Toast.Body>
                        </Toast>
                    );
                })}
            </ToastContainer>
        </ToastContext.Provider>
    );
};
